import React, { useState } from 'react';
import { isRepeating } from '../utils/habits.js';

/** Six-dot grip shown in edit mode. */
function Grip() {
  return (
    <svg width="12" height="18" viewBox="0 0 12 18" aria-hidden="true" className="text-ink-soft">
      {[3, 9, 15].map((y) =>
        [3, 9].map((x) => <circle key={`${x}-${y}`} cx={x} cy={y} r="1.4" fill="currentColor" />)
      )}
    </svg>
  );
}

/**
 * A single habit occurrence on one day.
 *
 * There is no checkbox: the habit is just handwriting on the page, and
 * clicking it strikes it through.
 *
 * Edit mode turns the row into a drag handle and reveals a delete button.
 *
 * @param {{habit: object, date: string, baseDate: string}} occurrence
 * @param {boolean} done
 * @param {boolean} editMode
 * @param {boolean} isDragSource card currently being dragged (rendered faded)
 * @param {() => void} onToggle
 * @param {(scope: 'day'|'series') => void} onDelete
 * @param {(event: React.PointerEvent) => void} onDragStart
 */
export default function HabitCard({
  occurrence,
  done,
  editMode,
  isDragSource,
  onToggle,
  onDelete,
  onDragStart,
}) {
  const { habit } = occurrence;
  const repeats = isRepeating(habit);
  // Repeating habits ask whether to remove one day or the whole series.
  const [confirming, setConfirming] = useState(false);

  const handleDelete = () => {
    if (repeats) setConfirming(true);
    else onDelete('day');
  };

  return (
    <div
      className={`-mx-1 flex items-center gap-2 rounded-lg px-1 py-0.5 transition-opacity ${
        isDragSource ? 'opacity-30' : 'opacity-100'
      } ${
        editMode
          ? 'cursor-grab select-none bg-paper-deep/50 no-touch-scroll active:cursor-grabbing'
          : ''
      }`}
      // In edit mode the row body initiates a drag; the buttons stop propagation.
      onPointerDown={editMode ? onDragStart : undefined}
    >
      {editMode && <Grip />}

      <button
        type="button"
        onClick={editMode ? undefined : onToggle}
        aria-pressed={editMode ? undefined : done}
        aria-label={editMode ? undefined : `Mark ${habit.name} as ${done ? 'not done' : 'done'}`}
        className="min-w-0 flex-1 text-left"
        tabIndex={editMode ? -1 : 0}
      >
        <span
          className={`block truncate font-hand text-lg leading-tight transition-colors ${
            done ? 'text-ink-soft line-through decoration-accent decoration-2' : ''
          }`}
        >
          {habit.name}
        </span>
      </button>

      {editMode && !confirming && (
        <button
          type="button"
          onPointerDown={(e) => e.stopPropagation()}
          onClick={handleDelete}
          aria-label={`Delete ${habit.name}`}
          className="shrink-0 rounded-pill px-1.5 text-xs text-accent hover:bg-accent-soft"
        >
          ✕
        </button>
      )}

      {editMode && confirming && (
        // Two-step delete for repeating habits.
        <div className="flex shrink-0 gap-1" onPointerDown={(e) => e.stopPropagation()}>
          <button
            type="button"
            onClick={() => onDelete('day')}
            className="rounded-pill border border-line px-1.5 py-0.5 text-[10px] uppercase"
          >
            day
          </button>
          <button
            type="button"
            onClick={() => onDelete('series')}
            className="rounded-pill border border-accent bg-accent px-1.5 py-0.5 text-[10px] uppercase text-white"
          >
            all
          </button>
          <button
            type="button"
            onClick={() => setConfirming(false)}
            aria-label="Cancel delete"
            className="px-1 text-[10px] text-ink-soft"
          >
            ↺
          </button>
        </div>
      )}
    </div>
  );
}
